import api from '../api/axiosConfig';

/** Whether a picked, pasted or dropped file is a PDF. */
export const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

export const formatBytes = (bytes: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAbsolute = (fileUrl: string) => /^https?:\/\//.test(fileUrl);

/**
 * A note's stored file as a Blob.
 *
 * Production hands out presigned R2 URLs, where the signature is the
 * credential and a plain fetch is right; local development stores files on
 * disk behind the authenticated /media/ route.
 */
export const fetchMediaBlob = async (fileUrl: string): Promise<Blob> => {
  if (isAbsolute(fileUrl)) {
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error(`Could not fetch attachment: ${response.status}`);
    return response.blob();
  }
  const filename = fileUrl.split('/').pop()?.split('?')[0];
  const response = await api.get(`/media/${filename}`, { responseType: 'blob' });
  return response.data;
};

/** Show the file in a new tab, where the browser's own PDF viewer takes it. */
export const openMedia = async (fileUrl: string) => {
  if (isAbsolute(fileUrl)) {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
    return;
  }
  // Opened before the fetch, while the click still counts as the user's:
  // a window opened after an await is a popup, and gets blocked.
  const win = window.open('', '_blank');
  const url = URL.createObjectURL(await fetchMediaBlob(fileUrl));
  if (win) win.location.href = url;
  else window.location.href = url;
};

/**
 * Save the file under the name it was uploaded with.
 *
 * Fetched first because <a download> is ignored for another origin, which
 * the R2 bucket is: the browser would open the file instead of saving it.
 */
export const downloadMedia = async (fileUrl: string, name: string) => {
  const url = URL.createObjectURL(await fetchMediaBlob(fileUrl));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Long enough for the download to have started
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
